import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AssetBadge,
  Button,
  Card,
  CheckIcon,
  CopyIcon,
  NetworkBadge,
} from '../components/ui';
import {
  HistoryFilters,
  filterByStatusAndPeriod,
  type PeriodFilter,
  type StatusFilter,
} from '../components/wallet/HistoryFilters';
import {
  TxHistoryRow,
  type TxStatus,
} from '../components/wallet/TxHistoryRow';
import {
  useDepositAddress,
  useDepositHistory,
  usePlatformConfig,
} from '../hooks/useApiQuery';
import { copyText } from '../lib/clipboard';
import { tg } from '../lib/tg';
import type { ChainInfo, DepositHistoryItem } from '../lib/types';

interface Props {
  embedded?: boolean;
}

export function Deposit({ embedded = false }: Props = {}) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const addr = useDepositAddress();
  const history = useDepositHistory();
  const platformConfig = usePlatformConfig();

  const address = addr.data?.address ?? '';
  const chains = addr.data?.supported_chains ?? [];

  const minDepositDisplay = (() => {
    const raw = platformConfig.data?.min_deposit_usd;
    if (!raw) return '50';
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  })();

  const pendingDeposit = history.data?.items.find(
    (d) => d.status === 'seen' || d.status === 'confirming',
  );

  const onCopyAddress = async () => {
    if (!address) return;
    const ok = await copyText(address);
    if (ok) {
      tg.hapticSuccess();
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      tg.hapticError();
    }
  };

  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const wrapperClass = embedded
    ? 'px-5 pt-2'
    : 'mx-auto max-w-app px-5 pt-6';

  return (
    <div className={wrapperClass}>
      <h2
        className={`pb-display leading-tight ${embedded ? 'text-[20px]' : 'text-[26px]'}`}
      >
        {t('deposit.title')}
      </h2>
      <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
        {t('deposit.subtitle_v2')}
      </p>

      <div className="mt-5 space-y-3">
        <AddressCard
          address={address}
          chains={chains}
          copied={copied}
          onCopy={onCopyAddress}
        />

        <Card pad="md" className="flex items-start gap-3 bg-pb-paper2">
          <span className="pb-display mt-0.5 text-[22px] text-pb-accent">
            ${minDepositDisplay}
          </span>
          <div>
            <div className="text-[14px] font-medium">
              {t('deposit.min_title')}
            </div>
            <p className="pb-caption mt-0.5">{t('deposit.min_body')}</p>
          </div>
        </Card>

        {pendingDeposit ? (
          <Card pad="md">
            <div className="flex items-start gap-3">
              <span className="pb-pulse mt-2 inline-block h-2 w-2 rounded-full bg-pb-warn text-pb-warn" />
              <div className="flex-1">
                <div className="text-[14px] font-medium">
                  {t('deposit.awaiting_title')}
                </div>
                <p className="pb-caption mt-0.5">
                  {t('deposit.status.confirming', {
                    c: pendingDeposit.confirmations,
                    r: pendingDeposit.confirmations_required,
                  })}{' '}
                  · +${Number(pendingDeposit.amount_usd).toFixed(2)}{' '}
                  {pendingDeposit.token_symbol}
                </p>
              </div>
            </div>
          </Card>
        ) : null}
      </div>

      <DepositHistorySection
        items={history.data?.items ?? []}
        locale={locale}
      />
    </div>
  );
}

function AddressCard({
  address,
  chains,
  copied,
  onCopy,
}: {
  address: string;
  chains: ChainInfo[];
  copied: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card pad="lg" variant="paper" className="pb-grain">
      <div className="pb-eyebrow">{t('deposit.your_address')}</div>
      <div className="mt-1.5 rounded-pb-md border border-pb-border-soft bg-tg-bg/70 px-4 py-4">
        <div className="break-all font-mono text-[13px] leading-relaxed text-tg-text">
          {address || '—'}
        </div>
      </div>
      <Button
        fullWidth
        variant={copied ? 'secondary' : 'primary'}
        onClick={onCopy}
        disabled={!address}
        className="mt-3"
        leading={copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
      >
        {copied ? t('common.copied') : t('deposit.copy_address')}
      </Button>

      {chains.length > 0 ? (
        <div className="mt-5">
          <div className="pb-eyebrow mb-2">
            {t('deposit.supported_networks')}
          </div>
          <ul className="space-y-2">
            {chains.map((chain) => (
              <li
                key={chain.chain_id}
                className="rounded-pb-md bg-pb-paper2 px-3 py-2.5"
              >
                {/* Stack vertically so a chain name like "Arbitrum One"
                    isn't elided to "Arbitr…" by the row's token chips
                    competing for horizontal space on narrow screens. */}
                <div className="flex items-center gap-2.5">
                  <NetworkBadge chainId={chain.chain_id} size={28} />
                  <span className="text-[14px] font-medium">
                    {chain.name}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
                  {chain.tokens.map((tk) => (
                    <span
                      key={tk.symbol}
                      className="inline-flex items-center gap-1 rounded-pb-sm border border-pb-border-soft bg-pb-paper px-2 py-0.5 font-medium"
                    >
                      <AssetBadge symbol={tk.symbol} size={14} />
                      {tk.symbol}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 rounded-pb-md border border-[color-mix(in_oklab,var(--pb-warn)_40%,transparent)] bg-[color-mix(in_oklab,var(--pb-warn)_10%,var(--pb-paper))] px-3 py-2.5">
        <p className="text-[12px] leading-relaxed text-tg-text">
          {t('deposit.warn_only_listed')}
        </p>
      </div>
    </Card>
  );
}

function DepositHistorySection({
  items,
  locale,
}: {
  items: DepositHistoryItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [period, setPeriod] = useState<PeriodFilter>('all');

  const filtered = useMemo(
    () =>
      filterByStatusAndPeriod(
        items,
        status,
        period,
        (s) => s === 'confirmed',
        (s) => s === 'failed' || s === 'reorg_dropped',
      ),
    [items, status, period],
  );

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="pb-eyebrow">{t('deposit.history')}</h2>
      </div>
      <div className="mb-3">
        <HistoryFilters
          status={status}
          period={period}
          statusOptions={[
            { value: 'all', label: t('history.filter.all') },
            { value: 'pending', label: t('history.filter.pending') },
            { value: 'done', label: t('history.filter.done') },
            { value: 'failed', label: t('history.filter.failed') },
          ]}
          periodOptions={[
            { value: 'all', label: t('history.filter.all_time') },
            { value: '7d', label: t('history.filter.last_7d') },
            { value: '30d', label: t('history.filter.last_30d') },
          ]}
          onStatusChange={setStatus}
          onPeriodChange={setPeriod}
        />
      </div>
      {filtered.length > 0 ? (
        <Card pad="none" variant="flat">
          <ul className="divide-y divide-pb-border-soft">
            {filtered.map((d) => (
              <li key={d.id}>
                <TxHistoryRow
                  kind="deposit"
                  chainId={d.chain_id}
                  asset={d.token_symbol}
                  amountUsd={d.amount_usd}
                  creditedUsd={null}
                  status={depositToStatus(d.status)}
                  createdAt={d.created_at}
                  confirmations={
                    d.status === 'confirming'
                      ? {
                          current: d.confirmations,
                          required: d.confirmations_required,
                        }
                      : undefined
                  }
                  locale={locale}
                />
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card pad="md" className="text-center">
          <p className="pb-caption">
            {items.length === 0
              ? t('deposit.no_deposits')
              : t('history.no_match')}
          </p>
        </Card>
      )}
    </section>
  );
}

function depositToStatus(s: DepositHistoryItem['status']): TxStatus {
  if (s === 'confirmed') return 'done';
  if (s === 'failed' || s === 'reorg_dropped') return 'failed';
  if (s === 'confirming') return 'confirming';
  return 'pending';
}
